<!-- SPDX-License-Identifier: AGPL-3.0-only -->

<script setup lang="ts">
import { computed, ref, type ComputedRef, type Ref } from "vue";
import { useRoute, useRouter, type RouteLocationNormalizedLoaded, type Router } from "vue-router";
import { ApiRequestError } from "../api/http.js";
import AppFooter from "../components/AppFooter.vue";
import ProductMark from "../components/ProductMark.vue";
import { useAuthenticationStore } from "../stores/authentication.js";

const authenticationStore: ReturnType<typeof useAuthenticationStore> = useAuthenticationStore();
const router: Router = useRouter();
const route: RouteLocationNormalizedLoaded = useRoute();
const email: Ref<string> = ref("");
const password: Ref<string> = ref("");
const submitting: Ref<boolean> = ref(false);
const errorMessage: Ref<string | null> = ref(null);
const backendUnavailable: ComputedRef<boolean> = computed(
  (): boolean => route.query.unavailable === "1",
);
const invitationAccepted: ComputedRef<boolean> = computed(
  (): boolean => route.query.invited === "1",
);
const passwordResetCompleted: ComputedRef<boolean> = computed(
  (): boolean => route.query.passwordReset === "1",
);

function readRedirect(): string {
  const redirect: unknown = route.query.redirect;
  return typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//")
    ? redirect
    : "/";
}

async function submitLogin(): Promise<void> {
  if (submitting.value) {
    return;
  }

  submitting.value = true;
  errorMessage.value = null;

  try {
    await authenticationStore.login(email.value, password.value);
    await router.replace(readRedirect());
  } catch (error: unknown) {
    if (error instanceof ApiRequestError && error.code === "INVALID_CREDENTIALS") {
      errorMessage.value = "The email address or password is incorrect.";
    } else if (error instanceof ApiRequestError && error.code === "RATE_LIMITED") {
      errorMessage.value = "Too many login attempts. Please wait a minute and try again.";
    } else {
      errorMessage.value = "The server could not be reached. Please try again.";
    }
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <main class="login-page">
    <section class="login-intro" aria-labelledby="product-message">
      <ProductMark />
      <div class="login-intro__content">
        <p class="eyebrow">Connection intelligence</p>
        <h1 id="product-message">See the gaps.<br /><span>Know the cause.</span></h1>
        <p class="login-intro__copy">
          Precise, self-hosted insight into the quality and availability of every connection you
          care about.
        </p>
      </div>
      <div class="signal-preview" aria-hidden="true">
        <span class="signal-preview__baseline"></span>
        <span class="signal-preview__pulse signal-preview__pulse--one"></span>
        <span class="signal-preview__pulse signal-preview__pulse--two"></span>
        <span class="signal-preview__pulse signal-preview__pulse--three"></span>
      </div>
      <p class="login-intro__footnote">Open source · Private by design · Built for clarity</p>
    </section>

    <section class="login-panel" aria-labelledby="login-title">
      <div class="login-card">
        <p class="eyebrow">Welcome back</p>
        <h2 id="login-title">Sign in to your account</h2>
        <p class="login-card__subtitle">Use your local LacunaScope credentials.</p>

        <div v-if="backendUnavailable" class="notice notice--warning" role="status">
          The API is currently unavailable. You can retry signing in when it is back online.
        </div>
        <div v-if="invitationAccepted" class="notice" role="status">
          Your account was created and the invitation was accepted. You can sign in now.
        </div>
        <div v-if="passwordResetCompleted" class="notice" role="status">
          Your password was changed. Sign in with the new password.
        </div>

        <form class="login-form" @submit.prevent="submitLogin">
          <label for="email">Email address</label>
          <input
            id="email"
            v-model="email"
            name="email"
            type="email"
            autocomplete="username"
            placeholder="admin@local"
            required
            autofocus
          />

          <label for="password">Password</label>
          <input
            id="password"
            v-model="password"
            name="password"
            type="password"
            autocomplete="current-password"
            placeholder="Enter your password"
            required
          />

          <p v-if="errorMessage" class="form-error" role="alert">{{ errorMessage }}</p>

          <button class="button button--primary" type="submit" :disabled="submitting">
            <span>{{ submitting ? "Signing in…" : "Sign in" }}</span>
            <span aria-hidden="true">→</span>
          </button>
        </form>
      </div>
      <AppFooter />
    </section>
  </main>
</template>
